import { useTranslations } from 'next-intl';
import Text from '@atoms/Text/Text';
import useCustomer from '@hooks/useCustomer';
import { isOfflineProduct, isOfflinePromotion, isOnlineProduct } from '@helpers/typeGuards/product';
import { dateToNumeric } from '@helpers/date';
import formatCurrency from '@helpers/formatCurrency';
import { formatPromotionComparePrice, isAtLowestHistoricalPrice } from '@helpers/productPromotion';
import type { AxfoodOfflineProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';

interface Props {
  product: AxfoodOfflineProductViewModel | AxfoodProductDetailsViewModel;
}

const SEPARATOR = ' • ';

const joinParts = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(SEPARATOR);

const ProductDetailsPriceAndPromotionDetails = ({ product }: Props) => {
  const t = useTranslations('product');
  const { customer } = useCustomer();
  const { price, potentialPromotions } = product;
  const promotion = potentialPromotions?.[0];
  const { redeemLimitLabel, timesUsed = 0, promotionRedeemLimit = 0, validUntil } = promotion ?? {};
  const isOffline = isOfflineProduct(product);
  const isOnline = isOnlineProduct(product);
  const { comparePrice, comparePriceUnit, savingsAmount, bargainProduct } = isOnline ? product : {};

  const showTimesUsed = isOffline ? !customer?.isAnonymous && promotionRedeemLimit > 0 : timesUsed > 0;
  const redeemLimitText = joinParts(
    redeemLimitLabel && t('promotion.limitHousehold', { redeemLimitLabel }),
    showTimesUsed && t('promotion.timesUsed', { timesUsed, redeemLimit: promotionRedeemLimit })
  );

  const lowestHistoricalPrice =
    isOnline && !isOfflinePromotion(promotion) ? promotion?.lowestHistoricalPrice : undefined;
  const offlinePromotionLowestHistoricalPrice =
    isOffline && isOfflinePromotion(promotion) ? product.offlinePromotionLowestHistoricalPrice : undefined;

  const savingsText =
    !!savingsAmount &&
    isAtLowestHistoricalPrice(product) &&
    t('promotion.saveKr', { amount: formatCurrency(savingsAmount) });

  const getPromotionComparePriceText = () => {
    if (!promotion || !('comparePrice' in promotion)) return undefined;
    const { comparePrice: promotionComparePrice } = promotion;
    if (!promotionComparePrice) return t('price.compare-missing');
    if (isOffline && isOfflinePromotion(promotion)) {
      return joinParts(`${t('price.compare')} ${promotionComparePrice}`, promotion.savePrice);
    }
    if (isOnline) {
      return joinParts(formatPromotionComparePrice(product, t), savingsText);
    }
    return undefined;
  };
  const promotionComparePriceText = getPromotionComparePriceText();

  const ordinaryComparePriceText =
    comparePrice &&
    comparePriceUnit &&
    !bargainProduct &&
    joinParts(
      `${t('price.compare')} ${comparePrice}/${comparePriceUnit}`,
      promotion && price && `${t('price.default')} ${price}`
    );

  return (
    <>
      {redeemLimitText && (
        <Text type="detail" color="red">
          {redeemLimitText}
        </Text>
      )}
      {validUntil && (
        <Text type="detail" color="red">
          {`${t('promotion.validUntil')} ${dateToNumeric(validUntil)}`}
        </Text>
      )}
      {promotionComparePriceText && (
        <Text type="detail" color="red">
          {promotionComparePriceText}
        </Text>
      )}
      {ordinaryComparePriceText && (
        <Text type="detail" color="darkerGray">
          {ordinaryComparePriceText}
        </Text>
      )}
      {lowestHistoricalPrice && !bargainProduct && (
        <Text type="detail" color="darkerGray">
          {t('price.lowestHistorical', { price: lowestHistoricalPrice.formattedValue ?? '' })}
        </Text>
      )}
      {offlinePromotionLowestHistoricalPrice && (
        <Text type="detail" color="darkerGray">
          {t('price.offlinePromotionLowestHistoricalPrice', { price: offlinePromotionLowestHistoricalPrice })}
        </Text>
      )}
    </>
  );
};

export default ProductDetailsPriceAndPromotionDetails;
